import React, { useEffect, useMemo, useState } from "react"

import gsap from "gsap"
import { AnimatePresence } from "framer-motion"
import { IconContext } from "react-icons"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ThemeProvider } from "@mui/material/styles"
import CssBaseline from "@mui/material/CssBaseline"

import { resolvePageBackground } from "theme/colors"
import { GlobalStyles } from "theme/global"
import { createMuiTheme } from "theme/muiTheme"

import Header from "components/organisms/header"
import Footer from "components/organisms/footer"
import CreateHead from "components/molecules/createHeader"
import Loader from "components/atoms/loader"

// Create a client
const queryClient = new QueryClient()

const getFirstDynamicBlock = (data) =>
  (data?.page?.dynamic_page?.content ?? []).find(Boolean)

/** White header elements at top when first block is Hero format A and page_settings.background is empty. */
const shouldUseLightHeader = (data, pageSettings) => {
  const first = getFirstDynamicBlock(data)
  if (!first?.fieldGroupName) return false

  const type = first.fieldGroupName.split("_").reverse()[0]
  const isHeroFormatA =
    type === "Hero" && first.format?.toUpperCase() === "A"
  const hasPageBackground = Boolean(pageSettings?.background)

  return isHeroFormatA && !hasPageBackground
}

const SCROLL_THRESHOLD = 150

const Layout = (props) => {
  const { location, data } = props

  console.log("[Layout] Props:", props)

  //STATE
  const [dimensions, setDimension] = useState({
    height: "",
    width: "",
  })

  //DEFS
  const pageSettings = props.pageContext?.page_settings
  const pageTranslation = props.pageContext?.translations?.[0]?.page_settings?.slug
  const lang = props.pageContext?.language?.code
  const pageBackground = resolvePageBackground(pageSettings?.background)
  const lightHeader = shouldUseLightHeader(data, pageSettings)
  const muiTheme = useMemo(
    () => createMuiTheme(pageBackground),
    [pageBackground]
  )
  const [scrolled, setScrolled] = useState(false)
  //DEFS

  //EFFECT — header bar transparency (all pages)
  useEffect(() => {
    let ticking = false

    const updateScrolled = () => {
      setScrolled(window.pageYOffset > SCROLL_THRESHOLD)
      ticking = false
    }

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrolled)
        ticking = true
      }
    }

    updateScrolled()
    window.addEventListener("scroll", onScroll, true)

    const cleanTransform = () => {
      if (
        !document?.querySelector(".main")?.children?.[1] ||
        !document?.querySelector(".main")?.children?.[1]?.style.transform
      ) {
        setTimeout(() => {
          cleanTransform()
        }, 800)
      } else {
        document.querySelector(".main").children[1].style.transform = "none"
      }
    }

    cleanTransform()

    return () => {
      window.removeEventListener("scroll", onScroll, true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  useEffect(() => {
    gsap.to("body", {
      duration: 0,
      css: { visibility: "visible", opacity: 1, overflowY: "auto" },
    })

    let vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty("--vh", `${vh}px`)

    const debouncedHandleResize = () =>
      setDimension({
        height: window.innerHeight,
        width: window.innerWidth,
      })

    window.addEventListener("resize", debouncedHandleResize)

    setDimension({
      height: window.innerHeight,
      width: window.innerWidth,
    })

    return () => {
      window.removeEventListener("resize", debouncedHandleResize)
    }
  }, [])

  //EFFECT

  //CHILDREN MODIFIER
  const childrenWithProps = React.Children.map(props.children, (child) => {
    return React.cloneElement(child, {
      dimensions: dimensions,
      lang: props.pageContext?.language?.code,
    })
  })
  //CHILDREN MODIFIER

  return (
    <>
      <GlobalStyles $pageBackground={pageBackground} />
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          <IconContext.Provider value={{ className: "react-icons" }}>
            <CreateHead
              title={pageSettings?.seoTitle}
              description={pageSettings?.seoDescription}
              image={pageSettings?.seoImage}
              lang={lang}
            />
            <Header
              variant="bar"
              lang={lang}
              scrolled={scrolled}
              lightHeader={lightHeader}
              dimensions={dimensions}
              navItems={props.pageContext?.navItems || []}
              siteSettings={props.pageContext?.siteSettings || {}}
              allLanguages={props.pageContext?.allLanguages || []}
            />
            <main className="main">
              <Loader />
              <AnimatePresence>{childrenWithProps}</AnimatePresence>
            </main>
            <Footer
              lang={lang}
              navItems={props.pageContext?.navItems || []}
              siteSettings={props.pageContext?.siteSettings || {}}
              allLanguages={props.pageContext?.allLanguages || []}
            />
            {/* <WhatsAppWidget
          phoneNo="351916197017"
          autoOpen={true}
          position="right"
          headerIcon="/android-chrome-192x192.png"
          chatPersonName="Carlos Dias"
          headerTitle="ACSolar"
          autoOpenTimer={5000}
          messageBox={true}
          widgetWidth="300px"
          widgetWidthMobile="260px"
          chatMessage={<>Em que posso ajudar?</>}
          placeholder="A sua mensagem..."
          btnTxt="Começar conversa"
        /> */}
          </IconContext.Provider>
        </QueryClientProvider>
      </ThemeProvider>
    </>
  )
}

export default Layout
